import { ComponentType, ReactNode } from 'react';
import { css } from '@emotion/react';
import {
  AudioLibraryUsage,
  CallRouteUsageType_Enum,
} from '@weave/schema-gen-ts/dist/schemas/phone/audio-library/v1/audio_library_service.pb';
import { CallGroupUsage } from '@weave/schema-gen-ts/dist/schemas/phone/callgroup/v1/callgroup_service.pb';
import { CallQueueUsage } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config_service.pb';
import { VoicemailUsage } from '@weave/schema-gen-ts/dist/schemas/phone/voicemail-config/v2/voicemail_config_service.pb';
import { PrimitiveType } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/primitive.pb';
import { AudioLibraryTypes } from '@frontend/api-audio-library';
import { useTranslation, Trans } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  ModalControlModalProps,
  Modal,
  Table,
  Text,
  IconButton,
  TrashIcon,
  ModalControlTriggerProps,
  SecondaryButton,
  useTooltip,
  ContentLoader,
  TextLink,
  Truncated,
} from '@frontend/design-system';
import { primitiveTypeTranslation } from '../../utils/phone-utils';

export const CanDeleteContent = ({
  modalProps,
  name,
  title,
  body,
  removeFn,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  title: string;
  body?: ReactNode;
  removeFn: () => void;
}) => {
  const { t } = useTranslation('phone');

  return (
    <>
      <Modal.Header onClose={modalProps.onClose}>{title}</Modal.Header>

      <Modal.Body>
        {body || (
          <Trans t={t} name={name}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
              <Text>
                Are you sure you want to delete{' '}
                <Text as='span' weight='semibold'>
                  {name}
                </Text>
                ?
              </Text>
              <Text>This action cannot be undone.</Text>
            </div>
          </Trans>
        )}
      </Modal.Body>
      <Modal.Actions
        primaryLabel={t('Yes, Delete')}
        onPrimaryClick={removeFn}
        secondaryLabel={t('No, Cancel')}
        onSecondaryClick={modalProps.onClose}
        destructive
      />
    </>
  );
};

export const CannotDeleteContent = ({
  modalProps,
  id,
  usage,
  title,
  body,
  disableLinks = false,
  showTable = false,
}: {
  modalProps: ModalControlModalProps;
  id?: string;
  usage: Usage[];
  title: string;
  body?: ReactNode;
  disableLinks?: boolean;
  showTable?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();
  const callObjectId = id;

  return (
    <>
      <Modal.Header onClose={modalProps.onClose}>{title}</Modal.Header>
      <Modal.Body>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {body}

          {showTable && usage.length > 0 ? (
            <Table
              data={usage}
              colConfig={[
                {
                  Header: t('Call Object Name'),
                  id: 'name',
                  accessor: (row) => row,
                  cellRenderer: ({ primitiveId, name, type, usageCount }) => {
                    const usageInstances = usageCount > 1 ? `(${usageCount} instances)` : undefined;

                    if (!disableLinks) {
                      switch (type) {
                        case PrimitiveType.PRIMITIVE_TYPE_INSTRUCTION_SET:
                          return (
                            <div
                              css={{
                                display: 'flex',
                                alignItems: 'center',
                                width: 'fit-content',
                                gap: theme.spacing(0.5),
                              }}
                            >
                              <Truncated
                                as={TextLink}
                                textAlign='left'
                                onClick={() =>
                                  navigate({
                                    to: '/phone/call-routes/:id',
                                    params: { id: primitiveId },
                                    search: { callObjectId },
                                  })
                                }
                                css={{ maxWidth: usageInstances ? 170 : 250 }}
                              >
                                {name}
                              </Truncated>
                              {usageInstances && (
                                <span css={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{usageInstances}</span>
                              )}
                            </div>
                          );
                        case PrimitiveType.PRIMITIVE_TYPE_CALL_QUEUE:
                          return (
                            <Truncated
                              as={TextLink}
                              textAlign='left'
                              onClick={() =>
                                navigate({
                                  to: '/phone/call-queues/:id',
                                  params: { id: primitiveId },
                                  search: { callObjectId },
                                })
                              }
                            >
                              {name}
                            </Truncated>
                          );
                        case PrimitiveType.PRIMITIVE_TYPE_TENANT:
                          return (
                            <Truncated
                              as={TextLink}
                              textAlign='left'
                              onClick={() => navigate({ to: '/phone/hold-music' })}
                            >
                              {name}
                            </Truncated>
                          );
                        case PrimitiveType.PRIMITIVE_TYPE_SITE:
                          return (
                            <Truncated
                              as={TextLink}
                              textAlign='left'
                              onClick={() => navigate({ to: '/phone/hold-music' })}
                            >
                              <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
                                {' '}
                                {name} <Icon name='location' />
                              </div>
                            </Truncated>
                          );
                      }
                    }
                    return <Truncated>{name}</Truncated>;
                  },
                },
                {
                  Header: t('Call Object Type'),
                  id: 'type',
                  accessor: (row) => row,
                  cellRenderer: (row: Usage) => {
                    if (row.callRouteUsageType === CallRouteUsageType_Enum.DEVICE_EXTENSION_ROUTE) {
                      return t('Device Route');
                    }

                    const translated = primitiveTypeTranslation[row.type];
                    return translated === 'Tenant' || translated === 'Site' ? 'Hold Music' : translated;
                  },
                },
              ]}
            />
          ) : (
            <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {usage.map(({ primitiveId, name, usageCount }) => (
                <div key={primitiveId} css={{ display: 'flex', gap: '4px', alignItems: 'center', width: '500px' }}>
                  <div css={{ flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Truncated
                      as={TextLink}
                      textAlign='left'
                      onClick={() =>
                        navigate({
                          to: '/phone/call-routes/:id',
                          params: { id: primitiveId! },
                          search: { callObjectId },
                        })
                      }
                    >
                      {name}
                    </Truncated>
                  </div>
                  {usageCount !== undefined && usageCount > 1 && (
                    <span css={{ whiteSpace: 'nowrap', flexShrink: 0 }}>({usageCount} instances)</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Actions showPrimary={false} secondaryLabel={t('Close')} onSecondaryClick={modalProps.onClose} />
    </>
  );
};

export const CannotDeleteContentV2 = ({
  modalProps,
  id,
  usages,
  title,
  body,
  disableLinks = false,
  showTable = false,
}: {
  modalProps: ModalControlModalProps;
  id?: string;
  // NOTE: AudioLibraryUsage has the same shape as VoicemailUsage, CallGroupUsage and CallQueueUsage so we can reuse it for all types of content
  usages: AudioLibraryUsage[];
  title: string;
  body?: ReactNode;
  disableLinks?: boolean;
  showTable?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();
  const callObjectId = id;

  const primitiveTypeLabels: Record<string, string> = {
    [AudioLibraryTypes.PrimitiveUsageType.IVR_MENU]: t('IVR Menu'),
    [AudioLibraryTypes.PrimitiveUsageType.CALL_QUEUE]: t('Call Queue'),
    [AudioLibraryTypes.PrimitiveUsageType.HOLD_MUSIC]: t('Hold Music'),
  };

  const getNavigateHandler = (usage: AudioLibraryUsage): (() => void) => {
    if (AudioLibraryTypes.isOverrideUsage(usage)) {
      return () =>
        navigate({
          to: '/phone/overrides',
          context: { overrideId: usage.overrideUsage.overrideId, openEditModal: true },
        });
    }
    if (AudioLibraryTypes.isCallRouteUsage(usage)) {
      return () =>
        navigate({
          to: '/phone/call-routes/:id',
          params: { id: usage.callRouteUsage.callRouteId },
          search: { callObjectId },
        });
    }
    if (AudioLibraryTypes.isPrimitiveUsage(usage)) {
      const { primitiveId, primitiveType } = usage.primitiveUsage;
      switch (primitiveType) {
        case AudioLibraryTypes.PrimitiveUsageType.IVR_MENU:
          return () =>
            navigate({
              to: '/phone/call-routes/:id',
              params: { id: primitiveId },
              search: { callObjectId },
            });
        case AudioLibraryTypes.PrimitiveUsageType.CALL_QUEUE:
          return () =>
            navigate({
              to: '/phone/call-queues/:id',
              params: { id: primitiveId },
              search: { callObjectId },
            });
        case AudioLibraryTypes.PrimitiveUsageType.HOLD_MUSIC:
          return () => navigate({ to: '/phone/hold-music' });
      }
    }
    return () => {};
  };

  const getUsageTypeLabel = (usage: AudioLibraryUsage): string => {
    if (AudioLibraryTypes.isOverrideUsage(usage)) {
      return t('Override');
    }
    if (AudioLibraryTypes.isCallRouteUsage(usage)) {
      return usage.callRouteUsage.usageType === CallRouteUsageType_Enum.DEVICE_EXTENSION_ROUTE
        ? t('Device Route')
        : t('Call Route');
    }
    if (AudioLibraryTypes.isPrimitiveUsage(usage)) {
      return primitiveTypeLabels[usage.primitiveUsage.primitiveType] ?? '';
    }
    return '';
  };

  return (
    <>
      <Modal.Header onClose={modalProps.onClose}>{title}</Modal.Header>
      <Modal.Body>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          {body}

          {showTable && usages.length > 0 ? (
            <Table
              data={usages}
              colConfig={[
                {
                  Header: t('Call Object Name'),
                  id: 'name',
                  accessor: (row) => row,
                  cellRenderer: (usage: AudioLibraryUsage) => {
                    const { name, usageCount } = AudioLibraryTypes.getUsageDetails(usage);
                    const usageInstances = usageCount && usageCount > 1 ? `(${usageCount} instances)` : undefined;

                    return (
                      <div
                        css={css`
                          display: flex;
                          gap: ${theme.spacing(1)};
                          overflow: hidden;
                        `}
                      >
                        <Truncated
                          as={disableLinks ? Text : TextLink}
                          onClick={disableLinks ? undefined : getNavigateHandler(usage)}
                          css={{ flex: 1, minWidth: 0 }}
                        >
                          {name}
                        </Truncated>
                        {usageInstances && (
                          <span
                            css={css`
                              white-space: nowrap;
                              flex-shrink: 0;
                            `}
                          >
                            {usageInstances}
                          </span>
                        )}
                      </div>
                    );
                  },
                },
                {
                  Header: t('Call Object Type'),
                  id: 'type',
                  accessor: (row) => row,
                  cellRenderer: (row: AudioLibraryUsage) => getUsageTypeLabel(row),
                },
              ]}
            />
          ) : (
            <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {usages.map((usage) => {
                const { name, id: primitiveId, usageCount } = AudioLibraryTypes.getUsageDetails(usage);

                return (
                  <div key={primitiveId} css={{ display: 'flex', gap: '4px', alignItems: 'center', width: '500px' }}>
                    <div css={{ flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
                      <Truncated
                        as={disableLinks ? Text : TextLink}
                        textAlign='left'
                        onClick={disableLinks ? undefined : getNavigateHandler(usage)}
                      >
                        {name}
                      </Truncated>
                    </div>
                    {usageCount !== undefined && usageCount > 1 && (
                      <span css={{ whiteSpace: 'nowrap', flexShrink: 0 }}>({usageCount} instances)</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        secondary={{
          label: t('Close'),
          onClick: modalProps.onClose,
        }}
      />
    </>
  );
};

export type Usage = {
  primitiveId: string;
  name: string;
  type: PrimitiveType;
  callRouteUsageType?: CallRouteUsageType_Enum;
  usageCount?: number;
};

export const DeleteModal = <
  TUsage extends Usage | AudioLibraryUsage | VoicemailUsage | CallGroupUsage | CallQueueUsage
>({
  modalProps,
  name,
  id,
  phoneTenantId,
  usage,
  isLoading,
  isFetching,
  isError,
  CannotDeleteContent,
  CanDeleteContent,
  disableLinks = false,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
  usage: TUsage[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  CannotDeleteContent: ComponentType<{
    id: string;
    modalProps: ModalControlModalProps;
    name: string;
    usage: TUsage[];
    disableLinks?: boolean;
  }>;
  CanDeleteContent: ComponentType<{
    modalProps: ModalControlModalProps;
    name: string;
    id: string;
    phoneTenantId: string;
  }>;
  disableLinks?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const hasUsage = !!usage.length;
  let content;

  if (isError) {
    content = (
      <>
        <Modal.Header onClose={modalProps.onClose}>{t('Delete')}</Modal.Header>
        <Modal.Body>
          <Text>{t('Failed to check usage. Please try again.')}</Text>
        </Modal.Body>
        <Modal.Actions showPrimary={false} onSecondaryClick={modalProps.onClose} secondaryLabel='Close' />
      </>
    );
    // This seems to work better with both isLoading and isFetching, presumable because `isFetching` is set when the query is refetched when stale
  } else if (isFetching || isLoading) {
    content = (
      <Modal.Body>
        <div style={{ height: 100, width: 200 }}>
          <ContentLoader show message={t('Checking usage')} />
        </div>
      </Modal.Body>
    );
  } else if (hasUsage) {
    content = (
      <CannotDeleteContent id={id} modalProps={modalProps} name={name} usage={usage} disableLinks={disableLinks} />
    );
  } else {
    content = <CanDeleteContent modalProps={modalProps} name={name} id={id} phoneTenantId={phoneTenantId} />;
  }

  return (
    <Modal {...modalProps} minWidth={600}>
      {content}
    </Modal>
  );
};

export const DeleteIconButton = ({
  label,
  trackingId,
  triggerProps,
  children,
  disabled,
}: {
  label: string;
  trackingId?: string;
  triggerProps: ModalControlTriggerProps;
  children: ReactNode;
  disabled?: boolean;
}) => {
  return (
    <>
      <IconButton
        size='xsmall'
        showLabelWhenDisabled
        showLabelOnHover={disabled}
        {...triggerProps}
        label={label}
        trackingId={trackingId}
        disabled={disabled}
      >
        <TrashIcon color={'light'} />
      </IconButton>
      {children}
    </>
  );
};

export const DeleteButton = ({
  label,
  trackingId,
  triggerProps,
  children,
  disabled,
  disabledTooltip,
}: {
  label: string;
  trackingId: string;
  triggerProps: ModalControlTriggerProps;
  children: ReactNode;
  disabled?: boolean;
  disabledTooltip?: string;
}) => {
  const { Tooltip, tooltipProps, triggerProps: tooltipTriggerProps } = useTooltip();

  return (
    <>
      <SecondaryButton
        size='large'
        destructive
        aria-disabled={disabled}
        {...(disabled ? tooltipTriggerProps : triggerProps)}
        css={[
          { width: 'fit-content' },
          /**
           * These styles are non-standard to enable a fake disabled button
           * The button is focusable, but not clickable
           */
          disabled && {
            cursor: 'not-allowed',
            borderColor: theme.colors.status.disabled,
            color: theme.colors.neutral20,

            ':hover,:focus': {
              background: 'inherit',
              borderColor: 'inherit',
            },

            ':active': {
              background: 'inherit',
              borderColor: 'inherit',
            },
          },
        ]}
        trackingId={trackingId}
      >
        {label}
      </SecondaryButton>
      <Tooltip {...tooltipProps}>{disabledTooltip}</Tooltip>
      {children}
    </>
  );
};

export const DeleteFlow = {
  Modal: DeleteModal,
  IconButton: DeleteIconButton,
  Button: DeleteButton,
  CanDeleteContent,
  CannotDeleteContent,
  CannotDeleteContentV2,
};
